/**
 * Verified by Avrio, checked in the browser (docs/work-replay.md, "Verified
 * by Avrio"). Rio signs each replay's manifest at `complete` with KMS: ECDSA
 * on P-256 over the SHA-256 of the manifest's exact bytes (MessageType
 * DIGEST), a DER signature in base64. Signing that digest verifies exactly as
 * ECDSA with SHA-256 over the bytes themselves, which is what WebCrypto
 * checks here.
 *
 * The public key is pinned in the build (REACT_APP_WORK_REPLAY_PUBLIC_KEY:
 * the key's SPKI DER, in base64), never taken from the data it checks; the
 * algorithm is fixed too, whatever the view says it is. So is the key's id
 * (REACT_APP_WORK_REPLAY_KEY_ID: its ARN, what KMS names as `keyId` in each
 * signature): a replay signed with another key (another environment's, or one
 * rotated in) is one this build can't check, never one that failed. A build
 * without both can't verify a replay, and says so.
 */

/** Does Avrio's signature check out over these bytes? `no_key`: there is no usable key to ask with. */
export type SignatureCheck = 'valid' | 'invalid' | 'no_key';

const P256_BYTES = 32;

/** The public key this build checks signatures with, or null when it has none. */
export const pinnedPublicKey = (): string | null =>
  (process.env.REACT_APP_WORK_REPLAY_PUBLIC_KEY ?? '').trim() || null;

/** The id (ARN) of the key this build checks signatures with, or null when it has none. */
export const pinnedKeyId = (): string | null =>
  (process.env.REACT_APP_WORK_REPLAY_KEY_ID ?? '').trim() || null;

/** Standard base64 (padded, as KMS and the build's key are), or null when it isn't. */
export const fromBase64 = (text: string): Uint8Array | null => {
  const clean = text.replace(/\s+/g, '');
  if (!clean || clean.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(clean))
    return null;
  try {
    return Uint8Array.from(atob(clean), (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
};

/**
 * A DER ECDSA signature, SEQUENCE { INTEGER r, INTEGER s }, as the raw r‖s
 * WebCrypto takes: each integer without its sign byte, left-padded to 32
 * bytes. Null when it isn't one, or an integer is negative or too long for
 * P-256.
 */
export const derToRaw = (der: Uint8Array): Uint8Array | null => {
  // A P-256 signature is at most 72 bytes, so every length fits in one byte (short form).
  if (
    der.length < 8 ||
    der[0] !== 0x30 ||
    der[1] >= 0x80 ||
    der[1] !== der.length - 2
  )
    return null;
  const raw = new Uint8Array(P256_BYTES * 2);
  let at = 2;
  for (let i = 0; i < 2; i += 1) {
    if (der[at] !== 0x02) return null;
    const length = der[at + 1];
    if (
      length === undefined ||
      length === 0 ||
      length >= 0x80 ||
      at + 2 + length > der.length
    )
      return null;
    let value = der.subarray(at + 2, at + 2 + length);
    if (value[0] & 0x80) return null;
    while (value.length > 1 && value[0] === 0) value = value.subarray(1);
    if (value.length > P256_BYTES) return null;
    raw.set(value, P256_BYTES * (i + 1) - value.length);
    at += 2 + length;
  }
  return at === der.length ? raw : null;
};

/** Checks a base64 DER signature over `bytes` against the pinned public key (base64 SPKI DER). Never throws. */
export const verifySignature = async (
  bytes: Uint8Array,
  signature: string,
  publicKey: string,
  subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle,
): Promise<SignatureCheck> => {
  const spki = fromBase64(publicKey);
  if (!spki || !subtle) return 'no_key';
  let key: CryptoKey;
  try {
    key = await subtle.importKey(
      'spki',
      spki as BufferSource,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    );
  } catch {
    return 'no_key';
  }
  const der = fromBase64(signature);
  const raw = der && derToRaw(der);
  if (!raw) return 'invalid';
  try {
    const ok = await subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      key,
      raw as BufferSource,
      bytes as BufferSource,
    );
    return ok ? 'valid' : 'invalid';
  } catch {
    return 'invalid';
  }
};
